/**
 * Which valve waters this probe. Once linked, the valve's runs are shaded on the
 * probe's chart, so a run the soil never answered (a blocked dripper, a valve
 * that opened onto nothing) stands out against one it did. Irrigation admin
 * seat to change; everyone sees it.
 *
 * Valves come from the irrigation overview, named as the other irrigation pages
 * name them; this probe's own farm first.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import irrigationOverviewService, { type IrrigationOverviewData } from '../../services/irrigationOverview';
import sensorDevicesService, { type ValveLink } from '../../services/sensorDevices';
import { valveOptions, type ValveOption } from '../../utils/soilMoisture';

const keyOf = (mac: string, relay: number) => `${mac.toUpperCase()}#${relay}`;

export default function ValveLinkCard({
  deviceId,
  farmId,
  channelKind,
  channelIndex,
  channelLabel,
  valve,
  canEdit,
  onSaved,
}: {
  deviceId: string;
  farmId: number | null;
  channelKind: string;
  channelIndex: number;
  channelLabel: string;
  valve: ValveLink | null;
  canEdit: boolean;
  onSaved: (valve: ValveLink | null) => void;
}) {
  const [overview, setOverview] = useState<IrrigationOverviewData | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState(valve ? keyOf(valve.router_mac, valve.relay_index) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    irrigationOverviewService
      .getOverview()
      .then((d) => { if (active) setOverview(d); })
      .catch(() => { if (active) setLoadFailed(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setDraft(valve ? keyOf(valve.router_mac, valve.relay_index) : '');
    setError(null);
  }, [valve, channelKind, channelIndex]);

  /** This farm's valves first, then every other farm's, grouped by farm. */
  const groups = useMemo(() => {
    if (!overview) return [] as Array<{ label: string; options: ValveOption[] }>;
    const farms = [...overview.farms].sort((a, b) => Number(b.id === farmId) - Number(a.id === farmId));
    const out = farms
      .map((f) => ({ label: f.name, options: valveOptions(f.controllers) }))
      .filter((g) => g.options.length > 0);
    const loose = valveOptions(overview.unassigned.controllers);
    if (loose.length) out.push({ label: 'Not on a farm', options: loose });
    return out;
  }, [overview, farmId]);

  const all = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const current = valve ? all.find((o) => keyOf(o.routerMac, o.relayIndex) === keyOf(valve.router_mac, valve.relay_index)) : null;
  const dirty = draft !== (valve ? keyOf(valve.router_mac, valve.relay_index) : '');

  const save = async () => {
    setSaving(true);
    setError(null);
    const opt = all.find((o) => keyOf(o.routerMac, o.relayIndex) === draft) ?? null;
    const next = opt ? { router_mac: opt.routerMac, relay_index: opt.relayIndex } : null;
    try {
      await sensorDevicesService.setChannelValve(deviceId, channelKind, channelIndex, next);
      onSaved(next);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Could not save the valve.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4 text-blue-600" />
          Watered by
          <span className="text-xs font-normal text-gray-500">for {channelLabel}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <p className="text-sm text-gray-600">
          {valve
            ? current
              ? <>Linked to <strong>{current.name}</strong> ({current.where}). Its runs are shaded blue on the chart.</>
              : 'Linked to a valve that is no longer on any of your controllers.'
            : 'Not linked. Choose the valve that waters this probe and its runs are shaded on the chart, so you can see whether the soil answered.'}
        </p>
        {loadFailed && <p className="text-sm text-red-600">Could not load your valves.</p>}
        {canEdit && overview && (
          <>
            {all.length === 0 ? (
              <p className="text-sm text-gray-500">No valves are on your controllers yet.</p>
            ) : (
              <label className="block min-w-0">
                <span className="text-xs font-medium text-gray-700">Valve</span>
                <select
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                >
                  <option value="">Not linked</option>
                  {groups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((o) => (
                        <option key={keyOf(o.routerMac, o.relayIndex)} value={keyOf(o.routerMac, o.relayIndex)}>
                          {o.name} — {o.where}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {all.length > 0 && (
              <Button size="sm" onClick={save} disabled={saving || !dirty}>
                <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : 'Save valve'}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
